import { ARCHETYPE_BY_ID } from "@nano/content";
import type { LeaderboardEntry } from "@nano/contracts";
import { useEffect, useRef, useState } from "react";
import { closeOverlay, refreshLeaderboard, rememberRank, updateMyRanking } from "../../app/actions.ts";
import { appStore, shallowEqual, useApp } from "../../app/store.ts";
import { houseName } from "../../i18n/content.ts";
import { locale } from "../../i18n/lang.ts";
import { vi } from "../../i18n/vi.ts";
import { play } from "../../services/audio/sfx.ts";
import { Art, Button, Sheet } from "../components/common.tsx";
import { centerOf, fx } from "../fx/fx.ts";

/** Top three on a podium: second, first, third. */
function Podium({ entries, publicTag }: { entries: readonly LeaderboardEntry[]; publicTag: string | null }) {
  const order = [entries[1], entries[0], entries[2]];
  return (
    <div className="podium" data-testid="podium">
      {order.map((entry, index) => {
        if (!entry) return <span key={index} className="podium__slot podium__slot--empty" />;
        const archetype = ARCHETYPE_BY_ID.get(entry.avatarId);
        return (
          <div key={entry.publicTag} className={"podium__slot podium__slot--" + entry.rank + (entry.publicTag === publicTag ? " podium__slot--me" : "")}>
            {archetype ? <Art id={archetype.bodyAssets.front} className="podium__avatar" /> : null}
            <span className="podium__name">{entry.displayName}</span>
            <span className="podium__score">
              <Art id="reward.star" className="board__star" />
              {entry.totalStars}
            </span>
            <span className="podium__block">{entry.rank}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Pause between manual updates, so the button cannot hammer the Worker. */
const UPDATE_COOLDOWN_MS = 10_000;

export function Leaderboard() {
  const { board, online, enabled, config, publicTag, myName } = useApp(
    (s) => ({
      board: s.save?.leaderboard ?? null,
      online: s.ui.online,
      enabled: s.ui.config.flags.leaderboardEnabled,
      config: s.ui.config,
      publicTag: s.save?.server?.publicTag ?? null,
      // Only a name the server has accepted; a rename still in its cooldown is not shown yet.
      myName: s.save && !s.save.profile.profileDirty ? s.save.profile.displayName : null,
    }),
    shallowEqual,
  );
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(true);
  const [delta, setDelta] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [coolingDown, setCoolingDown] = useState(false);
  const meRef = useRef<HTMLDivElement>(null);
  const alive = useRef(true);

  const load = (fetch: () => Promise<{ stale: boolean }>) => {
    const before = appStore.get().save?.prefs.lastRank ?? null;
    return fetch().then((result) => {
      if (!alive.current) return;
      setStale(result.stale);
      setLoading(false);
      const rank = appStore.get().save?.leaderboard?.me?.rank ?? null;
      if (rank !== null && before !== null && rank !== before) {
        setDelta(before - rank);
        if (rank < before) {
          play("record");
          const at = centerOf(meRef.current);
          if (at) setTimeout(() => fx.sparkles(at.x, at.y, 18, 160), 120);
        }
      }
      rememberRank(rank);
    });
  };

  useEffect(() => {
    alive.current = true;
    void load(() => refreshLeaderboard());
    return () => {
      alive.current = false;
    };
  }, []);

  const update = () => {
    setUpdating(true);
    setCoolingDown(true);
    setTimeout(() => alive.current && setCoolingDown(false), UPDATE_COOLDOWN_MS);
    void load(updateMyRanking).finally(() => alive.current && setUpdating(false));
  };

  // The top list can come from the Worker's short edge cache; the player's own
  // row always shows their accepted name and the live numbers from /leaderboard/me.
  const me = board?.me ?? null;
  const withMe = (entry: LeaderboardEntry): LeaderboardEntry =>
    entry.publicTag !== publicTag
      ? entry
      : {
          ...entry,
          ...(myName ? { displayName: myName } : {}),
          ...(me ? { totalStars: me.totalStars, totalCompleted: me.totalCompleted } : {}),
        };
  const entries = board?.top?.entries.map(withMe) ?? [];

  const fetchedAt = board ? new Date(board.fetchedAt).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <Sheet title={vi.leaderboard.title} onClose={() => closeOverlay()} className="board" testId="leaderboard">
      {!enabled ? <p className="note">{vi.leaderboard.disabled}</p> : null}
      {!online ? <p className="note">{vi.leaderboard.offline}</p> : null}
      {stale && board ? <p className="note">{vi.leaderboard.stale(fetchedAt)}</p> : null}
      {board?.me ? (
        <div className="board__me" data-testid="board-me" ref={meRef}>
          <Art id="icon.trophy" className="board__trophy" />
          <div>
            {myName ? <b className="board__myname">{myName}</b> : null}
            <strong>{board.me.beyondCap ? vi.leaderboard.beyond : vi.leaderboard.you(board.me.rank)}</strong>
            {delta !== 0 ? (
              <em className={"board__delta " + (delta > 0 ? "board__delta--up" : "board__delta--down")}>
                {delta > 0 ? vi.leaderboard.up(delta) : vi.leaderboard.down(-delta)}
              </em>
            ) : null}
            <span>
              {vi.leaderboard.summary(board.me.totalStars, board.me.totalCompleted)}
            </span>
          </div>
        </div>
      ) : null}
      {enabled ? (
        <Button variant="secondary" icon="icon.update" className="board__update" onClick={update} disabled={!online || updating || coolingDown} testId="board-update">
          {updating ? vi.leaderboard.updating : vi.leaderboard.update}
        </Button>
      ) : null}
      {board?.me ? (
        <details className="board__houses">
          <summary>{vi.leaderboard.houses}</summary>
          <ul>
            {config.houses.map((house) => {
              const progress = board.me?.houses.find((h) => h.houseId === house.id);
              return (
                <li key={house.id}>
                  <span>{houseName(house)}</span>
                  <span>
                    {house.status === "available"
                      ? vi.leaderboard.houseLine(progress?.completedCount ?? 0, progress?.starsTotal ?? 0)
                      : vi.construction.title}
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
      {loading && !board ? <p className="note">{vi.wait}</p> : null}
      {board?.top && board.top.entries.length === 0 ? <p className="note">{vi.leaderboard.empty}</p> : null}
      {entries.length > 0 ? <Podium entries={entries.slice(0, 3)} publicTag={publicTag} /> : null}
      <ol className="board__list">
        {entries.slice(3).map((entry) => {
          const archetype = ARCHETYPE_BY_ID.get(entry.avatarId);
          return (
            <li key={entry.publicTag} className={`board__row ${entry.publicTag === publicTag ? "board__row--me" : ""}`}>
              <span className="board__rank">{entry.rank}</span>
              {archetype ? <Art id={archetype.bodyAssets.front} className="board__avatar" /> : null}
              <span className="board__name">
                {entry.displayName}
                <small>{entry.publicTag}</small>
              </span>
              <span className="board__score">
                <Art id="reward.star" className="board__star" />
                {entry.totalStars}
                <small>
                  {entry.totalCompleted} {vi.leaderboard.levels}
                </small>
              </span>
            </li>
          );
        })}
      </ol>
    </Sheet>
  );
}
